import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../button';
import { Switch } from '../switch';
import { Label } from '../label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../dialog';
import { useAnalytics } from '../../../lib/AnalyticsProvider';

export function CookieConsent() {
  const navigate = useNavigate();
  const { updateConsentPreferences, trackConsentEvent } = useAnalytics();
  
  const [showBanner, setShowBanner] = useState(false);
  const [preferences, setPreferences] = useState({
    analytics: false,
    marketing: false,
    functional: true
  });
  const [showDetails, setShowDetails] = useState(false);
  const [localPreferences, setLocalPreferences] = useState(preferences);

  // Check if consent has already been given
  useEffect(() => {
    const checkConsentStatus = () => {
      try {
        const stored = localStorage.getItem('cookie-consent-preferences');
        const consentGiven = localStorage.getItem('cookie-consent-given');
        
        if (stored && consentGiven) {
          const storedPreferences = JSON.parse(stored);
          setPreferences(storedPreferences);
          setLocalPreferences(storedPreferences);
          setShowBanner(false);
        } else {
          setShowBanner(true);
        }
      } catch (error) {
        console.error('Error checking consent status:', error);
        setShowBanner(true);
      }
    };

    checkConsentStatus();
  }, []);

  const updatePreferences = (newPrefs: any) => {
    setPreferences(newPrefs);
    setLocalPreferences(newPrefs);
  };

  const acceptAll = async () => {
    const newPrefs = { analytics: true, marketing: true, functional: true };
    setPreferences(newPrefs);
    await updateConsentPreferences(newPrefs);
    localStorage.setItem('cookie-consent-given', 'true');
    
    // Track consent events
    trackConsentEvent('consent_given', 'analytics', true);
    trackConsentEvent('consent_given', 'marketing', true);
    trackConsentEvent('consent_given', 'functional', true);
    
    setShowBanner(false);
  };

  const rejectAll = async () => {
    const newPrefs = { analytics: false, marketing: false, functional: true };
    setPreferences(newPrefs);
    await updateConsentPreferences(newPrefs);
    localStorage.setItem('cookie-consent-given', 'true');
    
    // Track consent events
    trackConsentEvent('consent_denied', 'analytics', false);
    trackConsentEvent('consent_denied', 'marketing', false);
    trackConsentEvent('consent_given', 'functional', true);
    
    setShowBanner(false);
  };

  const savePreferences = async () => {
    await updateConsentPreferences(localPreferences);
    localStorage.setItem('cookie-consent-given', 'true');
    
    // Track individual consent events
    trackConsentEvent('consent_updated', 'analytics', localPreferences.analytics);
    trackConsentEvent('consent_updated', 'marketing', localPreferences.marketing);
    trackConsentEvent('consent_updated', 'functional', localPreferences.functional);
    
    setShowBanner(false);
  };

  const handleSavePreferences = async () => {
    await savePreferences();
    setShowDetails(false);
  };

  const handleAcceptAll = async () => {
    await acceptAll();
  };

  const handleRejectAll = async () => {
    await rejectAll();
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50">
      {showDetails ? (
        <Dialog open={showDetails} onOpenChange={setShowDetails}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Nastavenia cookies</DialogTitle>
            </DialogHeader>
            <div className="space-y-6 py-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="font-bold">Nevyhnutné cookies</Label>
                  <p className="text-sm text-muted-foreground">
                    Tieto cookies sú potrebné pre správne fungovanie stránky. Nie je možné ich vypnúť.
                  </p>
                </div>
                <Switch checked={true} disabled />
              </div>
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="font-bold">Analytické cookies</Label>
                  <p className="text-sm text-muted-foreground">
                    Pomáhajú nám pochopiť, ako používatelia interagujú s našou stránkou.
                  </p>
                </div>
                <Switch
                  checked={localPreferences.analytics}
                  onCheckedChange={(checked: boolean) => {
                    updatePreferences({ analytics: checked });
                    setLocalPreferences(prev => ({ ...prev, analytics: checked }));
                  }}
                />
              </div>
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="font-bold">Marketingové cookies</Label>
                  <p className="text-sm text-muted-foreground">
                    Umožňujú zobrazovať relevantnejšie reklamy na základe vašich záujmov.
                  </p>
                </div>
                <Switch
                  checked={localPreferences.marketing}
                  onCheckedChange={(checked: boolean) => {
                    updatePreferences({ marketing: checked });
                    setLocalPreferences(prev => ({ ...prev, marketing: checked }));
                  }}
                />
              </div>
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="font-bold">Funkčné cookies</Label>
                  <p className="text-sm text-muted-foreground">
                    Pomáhajú zapamätať si vaše nastavenia a prispôsobiť stránku vašim preferenciám.
                  </p>
                </div>
                <Switch
                  checked={localPreferences.functional}
                  onCheckedChange={(checked: boolean) => {
                    updatePreferences({ functional: checked });
                    setLocalPreferences(prev => ({ ...prev, functional: checked }));
                  }}
                />
              </div>

              <div className="flex flex-wrap gap-2">
                <Button variant="secondary" onClick={handleRejectAll}>
                  Odmietnuť všetko
                </Button>
                <Button variant="outline" onClick={() => navigate('/cookies')}>
                  Viac informácií
                </Button>
                <Button variant="default" onClick={handleSavePreferences}>
                  Uložiť nastavenia
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      ) : (
        <div className="bg-background text-foreground border-t border-border p-6 shadow-lg">
          <div className="container mx-auto max-w-7xl">
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
              <div className="space-y-3">
                <h2 className="text-lg font-semibold">Používame cookies 🍪</h2>
                <p className="text-sm text-muted-foreground max-w-2xl">
                  Táto stránka používa cookies na zlepšenie vášho používateľského zážitku.{' '}
                  <button 
                    onClick={() => navigate('/cookies')} 
                    className="text-primary hover:underline"
                  > 
                    Viac informácií
                  </button>
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" onClick={() => setShowDetails(true)}>
                  Nastaviť
                </Button>
                <Button variant="secondary" onClick={handleRejectAll}>
                  Odmietnuť všetko
                </Button>
                <Button variant="default" onClick={handleAcceptAll}>
                  Prijať všetko
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}